import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClaudeBotUsage, ClaudeUsageRange } from '../types/api.js';
import { ErrorState } from '../components/feedback/ErrorState/index.js';
import { useClaudeUsage } from '../hooks/useClaudeUsage.js';

/**
 * @graph-node {Page}
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business bot 別 Claude 利用状況ページ。母艦の bot が Grafana Cloud へ送る OTel テレメトリを読み、トークン消費・コスト・ツール使用を bot ごとに一覧する。認証済み社員全員が閲覧可。
 * @graph-connects useClaudeUsage [calls] bot 別利用状況の取得フック
 */
export const Route = createFileRoute('/claude-usage')({
  component: ClaudeUsagePage,
});

/** 選べる集計期間。 @graph-connects none */
const RANGES: ClaudeUsageRange[] = ['1h', '24h', '7d'];

/**
 * Claude Code が MCP 由来のツールを送るときの名前。
 *
 * **個別のサーバー名・ツール名は落とされる**（実測）。この行は「MCP 全体の合計」であって
 * 特定のツールではないため、画面でそう明示しないと `Bash` と同列の 1 ツールに見えてしまう。
 *
 * @graph-connects none
 */
const MCP_TOOL_NAME = 'mcp_tool';

/** 大きい数を読みやすく丸める。 @graph-connects none */
function formatCount(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(Math.round(value));
}

/** トークン合計。 @graph-connects none */
function totalTokens(usage: ClaudeBotUsage): number {
  const { input, output, cacheRead, cacheCreation } = usage.tokens;
  return input + output + cacheRead + cacheCreation;
}

/**
 * bot 別 Claude 利用状況ページ。
 *
 * @graph-business bot 別の Claude 利用状況を表示する画面コンポーネント。
 * @graph-connects useClaudeUsage [calls] 利用状況の取得
 */
function ClaudeUsagePage() {
  const { t } = useTranslation();
  const [range, setRange] = useState<ClaudeUsageRange>('24h');
  const { data, isLoading, error, refetch } = useClaudeUsage(range);

  if (error) {
    return <ErrorState message={t('claudeUsage.loadError')} onRetry={() => void refetch()} />;
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">{t('claudeUsage.title')}</h1>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">
            {t('claudeUsage.description')}
          </p>
        </div>
        <div className="flex gap-1">
          {RANGES.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => {
                setRange(value);
              }}
              className={
                value === range
                  ? 'rounded bg-[var(--color-primary)] px-3 py-1 text-sm text-white'
                  : 'rounded border border-[var(--color-border)] px-3 py-1 text-sm'
              }
            >
              {t(`claudeUsage.range.${value}`)}
            </button>
          ))}
        </div>
      </div>

      {data?.unavailable !== undefined && (
        <div className="mb-4 rounded border border-[var(--color-warning)] p-3 text-sm">
          {t('claudeUsage.unavailable', { reason: data.unavailable })}
        </div>
      )}

      {isLoading && <p className="text-sm">{t('common.loading')}</p>}

      {data !== undefined && data.unavailable === undefined && data.bots.length === 0 && (
        <p className="text-sm text-[var(--color-text-muted)]">{t('claudeUsage.empty')}</p>
      )}

      <div className="space-y-4">
        {(data?.bots ?? []).map((usage) => (
          <section
            key={`${usage.bot}-${usage.mode ?? ''}`}
            className="rounded border border-[var(--color-border)] p-4"
          >
            <header className="mb-3 flex items-baseline gap-2">
              <h2 className="font-bold">{usage.bot}</h2>
              {usage.mode !== undefined && (
                <span className="text-sm text-[var(--color-text-muted)]">{usage.mode}</span>
              )}
              <span className="ml-auto text-sm">
                {t('claudeUsage.summary', {
                  tokens: formatCount(totalTokens(usage)),
                  cost: usage.costUsd.toFixed(2),
                  sessions: Math.round(usage.sessions),
                })}
              </span>
            </header>

            <dl className="mb-3 grid grid-cols-4 gap-2 text-sm">
              {/*
                4 種を並べて出すのは、cacheRead が支配的で input / output だけ見ると実態を
                見誤るため。実測では cacheRead が input の 3 万倍近くになる場面があった。
              */}
              <div>
                <dt className="text-[var(--color-text-muted)]">{t('claudeUsage.token.input')}</dt>
                <dd>{formatCount(usage.tokens.input)}</dd>
              </div>
              <div>
                <dt className="text-[var(--color-text-muted)]">{t('claudeUsage.token.output')}</dt>
                <dd>{formatCount(usage.tokens.output)}</dd>
              </div>
              <div>
                <dt className="text-[var(--color-text-muted)]">
                  {t('claudeUsage.token.cacheRead')}
                </dt>
                <dd>{formatCount(usage.tokens.cacheRead)}</dd>
              </div>
              <div>
                <dt className="text-[var(--color-text-muted)]">
                  {t('claudeUsage.token.cacheCreation')}
                </dt>
                <dd>{formatCount(usage.tokens.cacheCreation)}</dd>
              </div>
            </dl>

            {usage.tools.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)]">
                {t('claudeUsage.noToolUsage')}
              </p>
            ) : (
              <ul className="flex flex-wrap gap-2 text-sm">
                {usage.tools.map((tool) => (
                  <li
                    key={tool.name}
                    className="rounded bg-[var(--color-surface-muted)] px-2 py-1"
                    title={tool.name === MCP_TOOL_NAME ? t('claudeUsage.mcpAggregated') : undefined}
                  >
                    <span>
                      {tool.name === MCP_TOOL_NAME ? t('claudeUsage.mcpToolLabel') : tool.name}
                    </span>
                    <span className="ml-1 font-bold">{formatCount(tool.count)}</span>
                    {tool.failures > 0 && (
                      <span className="ml-1 text-[var(--color-error)]">
                        {t('claudeUsage.toolFailures', { count: Math.round(tool.failures) })}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      {data !== undefined && (
        <p className="mt-4 text-xs text-[var(--color-text-muted)]">
          {t('claudeUsage.fetchedAt', { at: new Date(data.fetchedAt).toLocaleString() })}
        </p>
      )}
    </div>
  );
}
